import { FileText, Activity } from 'lucide-react';
import type { Patient } from '../../../types';
import { formatDisplayDate } from '../../../utils/displayDate';
import { Markdown } from '../../../components/Markdown/Markdown';
import './MolecularResultsSummary.scss';

interface MolecularResultsSummaryProps {
  patient: Patient;
}

/**
 * Molecular / genomic results summary. Renders the structured extraction produced
 * by the summarizer (test platform, detected/not-detected alterations, and
 * immunotherapy biomarkers). Degrades gracefully for germline panels that report
 * no tumor alterations. Falls back to the raw narrative when no structured data.
 *
 * @param patient - The patient hydrated with `molecular` from the document summary
 */
export function MolecularResultsSummary({ patient }: MolecularResultsSummaryProps) {
  const molecular = patient.molecular;
  const narrative = patient.narrative;

  if (!molecular) {
    return (
      <div className="molecular-results-report">
        <div className="molecular-results-section">
          <p style={{ color: 'var(--color-text-muted)' }}>
            {narrative || 'No molecular results available for this patient.'}
          </p>
        </div>
      </div>
    );
  }

  const hasAlterations = molecular.detected.length > 0 || molecular.notDetected.length > 0;

  return (
    <div className="molecular-results-report">
      {/* Report Header */}
      <div className="molecular-results-header">
        <div className="molecular-results-title">
          <FileText className="molecular-results-icon" />
          <span className="molecular-results-title-text">Report summary</span>
          <span className="molecular-results-subtitle">
            {molecular.testPlatform || 'Molecular Report'}
            {molecular.reportDate ? ` - ${formatDisplayDate(molecular.reportDate)}` : ''}
          </span>
        </div>
      </div>

      {/* Test Information */}
      <div className="molecular-results-info">
        <div className="molecular-results-grid">
          <div>
            <p className="molecular-results-label">Test Platform</p>
            <p className="molecular-results-value">{molecular.testPlatform || 'N/A'}</p>
          </div>
          <div>
            <p className="molecular-results-label">Sample Type</p>
            <p className="molecular-results-value">{molecular.sampleType || 'N/A'}</p>
          </div>
          <div>
            <p className="molecular-results-label">Collection Date</p>
            <p className="molecular-results-value">
              {formatDisplayDate(molecular.sampleCollectionDate, 'N/A')}
            </p>
          </div>
        </div>
      </div>

      {/* Genomic Alterations */}
      {hasAlterations && (
        <div className="molecular-results-section">
          <div className="molecular-results-section-header">
            <h3 className="molecular-results-section-title">Genomic Alterations</h3>
          </div>

          <div className="molecular-genes-grid">
            {molecular.detected.map((gene, index) => (
              <div key={`d-${index}`} className="molecular-gene-card molecular-gene-card-detected">
                <div className="molecular-gene-card-top">
                  <span className="molecular-gene-name">{gene.gene}</span>
                  {gene.alterationType && (
                    <span className="molecular-tag molecular-tag-mutation">{gene.alterationType}</span>
                  )}
                </div>
                <div className="molecular-gene-status molecular-gene-status-detected">
                  <span className="molecular-status-dot molecular-status-dot-detected"></span>
                  Detected
                </div>
              </div>
            ))}

            {molecular.notDetected.map((gene, index) => (
              <div key={`nd-${index}`} className="molecular-gene-card">
                <div className="molecular-gene-card-simple">
                  <span className="molecular-gene-name">{gene.gene}</span>
                  <span className="molecular-gene-status molecular-gene-status-not-detected">
                    <span className="molecular-status-dot molecular-status-dot-not-detected"></span>
                    Not detected
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Biomarkers & Immunotherapy Markers */}
      {molecular.biomarkers.length > 0 && (
        <div className="molecular-results-section">
          <h3 className="molecular-results-section-title">Biomarkers &amp; Receptors</h3>
          <div className="molecular-biomarkers-grid">
            {molecular.biomarkers.map((biomarker, index) => (
              <div key={index} className="molecular-biomarker-item">
                <span className="molecular-biomarker-label">{biomarker.label}:</span>
                <span className="molecular-biomarker-value">{biomarker.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Narrative summary */}
      {narrative && (
        <div className="molecular-results-section">
          <div className="molecular-results-section-header">
            <Activity className="molecular-profile-icon" />
            <h3 className="molecular-results-section-title">Summary</h3>
          </div>
          <div className="molecular-profile-summary">
            <Markdown className="molecular-profile-text">{narrative}</Markdown>
          </div>
        </div>
      )}

      {!hasAlterations && molecular.biomarkers.length === 0 && !narrative && (
        <div className="molecular-results-section">
          <p style={{ color: 'var(--color-text-muted)' }}>
            No genomic alterations or biomarkers were reported on this panel.
          </p>
        </div>
      )}
    </div>
  );
}
